import {
  UpcomingChargesQueryQuery,
  UpcomingChargesQueryQueryVariables
} from "@outschool/gql-frontend-generated";
import { startOfIsoWeekUtc } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { sortBy } from "lodash";
import React from "react";

export const UPCOMING_CHARGES_QUERY = gql`
  query UpcomingChargesQuery(
    $start: DateTime!
    $end: DateTime!
    $includeLearner: Boolean!
  ) {
    currentUser {
      uid
      activeEnrollments(start: $start, end: $end) {
        uid
        canUseEsaMembership
        upcomingCharges {
          date
          priceCents
          priceCredits
        }
        payments {
          credit_account_type
        }
        learner @include(if: $includeLearner) {
          uid
          name
        }
      }
    }
  }
`;

export type UpcomingChargeType = {
  priceCents: number;
  priceCredits: number;
  numEnrollments: number;
  date: string;
};

export const useUpcomingChargesByWeek = (
  start: Date | null,
  end: Date | null,
  skip?: boolean
) => {
  const { data, loading, error } = useQueryWithPreviousData<
    UpcomingChargesQueryQuery,
    UpcomingChargesQueryQueryVariables
  >(UPCOMING_CHARGES_QUERY, {
    variables: {
      start,
      end,
      includeLearner: false
    },
    skip: !start || !end || skip
  });

  const upcomingChargesByWeek = React.useMemo(() => {
    if (!data) {
      return [];
    }

    const aggregatedData: {
      [date: string]: {
        priceCents: number;
        priceCredits: number;
        numEnrollments: number;
      };
    } = {};

    data.currentUser?.activeEnrollments?.forEach(enrollment => {
      enrollment?.upcomingCharges.forEach(charge => {
        const isoWeekStart = startOfIsoWeekUtc(
          new Date(charge.date)
        ).toISOString();

        if (!aggregatedData[isoWeekStart]) {
          aggregatedData[isoWeekStart] = {
            priceCents: 0,
            priceCredits: 0,
            numEnrollments: 0
          };
        }

        aggregatedData[isoWeekStart].priceCents += charge.priceCents;
        aggregatedData[isoWeekStart].priceCredits += charge.priceCredits;
        aggregatedData[isoWeekStart].numEnrollments += 1;
      });
    });

    return sortBy(
      Object.entries(aggregatedData).map(([date, values]) => ({
        date,
        ...values
      })),
      "date"
    );
  }, [data]);

  return {
    upcomingChargesByWeek,
    loading,
    error
  };
};
